import { CaptureScreen } from "../design-system/shell/capture-screen";
import { Spinner } from "../design-system/primitives/spinner";
import { ActionButton } from "../design-system/concept/action-button";
import { ArrowForwardIcon } from "../design-system/shell/icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../design-system/primitives/alert-dialog";
import { useHistory, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import "../theme/Results.css";

import "../theme/Utilities.css";
import { useParams } from "react-router";
import { MeasurementResource, ObjectModel } from "../lib/types";
import { AvatarSection } from "../components/dashboard/avatar-section";
import { AuthProvider, saveTask, getMeasurement, deleteMeasurement } from "../lib/http-service";
import { getConfigForExperience, KeyLabel } from "../lib/domainConfig";
import { useAuth } from "../context/AuthContext";
import { dashboardHrefAfterScan } from "../lib/scan/dashboardLink";
import { ClientDataHolder } from "../lib/dashboard/data";
import { SessionData } from "../lib/schemas";
import { getSession } from "../lib/http-service";
import { getMeasurementFromModel } from "../lib/measure/measureCache";
import { calculateBodyFat } from "../lib/dashboard/calculate-bf";
import {
  calculateMuscleMass,
  resolveAgeYears,
} from "../lib/dashboard/calculate-muscle";
import { MeasureData } from "../lib/interfaces";
import { muscleMeasurements } from "../lib/constants";
const ResultPage: React.FC = () => {
  //const auth = new AuthProvider(useAuth0().getAccessTokenSilently);
  const auth = new AuthProvider(
    () => Promise.resolve("fix-ugly-later"),
    undefined,
    undefined,
    undefined,
    true
  );

  const history = useHistory();
  const { profileID, sessionID, gender } = useParams<{
    profileID: string;
    sessionID: string;
    gender: string;
  }>();
  const [session, setSession] = useState<SessionData>();
  // A coach who scans is signed in; a client on a scan link is not. "View
  // dashboard" sends each to their own view.
  const { isAuthenticated } = useAuth();

  const [client] = useState<ClientDataHolder>(
    new ClientDataHolder(profileID, auth)
  );
  const getResource = client.getResourceFromSession;
  const [isFetched, setIsFetched] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // The saved measurement's id, to discard by. saveTaskData stores it under
  // the session id; the response says so, and the session id is the fallback.
  const [measurementID, setMeasurementID] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [discarded, setDiscarded] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  // The fetch loop runs in an interval and cannot read state that changed
  // after it started, so the two facts it must not get wrong live in refs: a
  // scan saved before (opened again later) and a save already under way.
  const alreadySavedRef = useRef(false);
  const saveStartedRef = useRef(false);
  // The scan link's code, carried through from the scan page so "Scan again"
  // can sign in the same way the first scan did.
  const code = new URLSearchParams(useLocation().search).get("code");
  const [visibleMeasurements, setVisibleMeasurements] = useState<KeyLabel[]>(
    []
  );
  const [showMuscleMass, setShowMuscleMass] = useState(false);
  // The fetch loop needs this before React has flushed the state update above.
  const showMuscleMassRef = useRef(false);
  const [objectModel, setObjectModel] = useState<ObjectModel | undefined>();

  const [measurements, setMeasurements] = useState<MeasurementResource>({
    id: "",
    createdAt: "",
    height: 0,
    head: 0,
    neck: 0,
    chest: 0,
    waist: 0,
    buttocks: 0,
    leg_inner: 0,
    arm_outer: 0,
  });

  // Muscle mass is gated to the dev experience and needs weight, body fat, age
  // and height. Append the rows only when it is on and could be estimated.
  const hasMuscleEstimate = (measurements.muscle_mass ?? 0) > 0;
  const displayedMeasurements =
    showMuscleMass && hasMuscleEstimate
      ? [...visibleMeasurements, ...muscleMeasurements]
      : visibleMeasurements;

  const fetchModel = async (): Promise<ObjectModel> => {
    const session: SessionData = await getSession(profileID, sessionID, auth);
    setSession(session);

    // Check if this session's measurement was already saved
    const existing = await getMeasurement(profileID, sessionID, auth);
    if (existing) {
      alreadySavedRef.current = true;
      setIsSaved(true);
      setMeasurementID(existing.ID);
    }

    const displayConfig = getConfigForExperience(session.experience);
    showMuscleMassRef.current = displayConfig.showMuscleMass;
    setShowMuscleMass(displayConfig.showMuscleMass);
    return await getResource(sessionID, "model")
      .then((data) => {
        if (
          data["gender"] === "female" ||
          (data["gender"] && data["gender"]["value"] === "female")
        ) {
          setVisibleMeasurements(displayConfig.measurements.female);
        } else {
          setVisibleMeasurements(displayConfig.measurements.male);
        }
        return data;
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fetchMeasurements = async (): Promise<MeasurementResource> => {
    const heightInput = await getResource(sessionID, "height_input").then(
      (res) => res.value
    );

    return await getResource(sessionID, "measurements")
      .then((data) => {
        const scale = (heightInput * 100) / data.height;
        [
          "height",
          "head",
          "neck",
          "chest",
          "waist",
          "buttocks",
          "leg_inner",
          "arm_outer",
          "arm_upper",
          "arm_circ",
          "leg_circ",
          "waist_female",
        ].forEach((key) => {
          data[key] = data[key] * scale;
        });
        data.scale = scale * 10;

        return data;
      })
      .catch((err) => {
        console.log(err);

        throw new Error(`Error fetching measurements: ${err}`);
      });
  };

  /**
   * Store the scan — with the figures computed here, never the raw ones.
   *
   * Takes the measurements as an argument rather than reading state, because
   * it runs from the fetch loop the moment the calculations above finish: the
   * height rescale, the female waist measured off the mesh, body fat and muscle
   * mass all happen in this page, and a save that read state could catch the
   * empty initial value. Called from the end of that block, the order is fixed
   * by where the call is.
   */
  const saveModel = async (measurements: MeasurementResource) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const measureData: MeasureData = {
        ID: "",
        profileID: profileID,
        client: profileID,
        createdAt: session?.createdAt || new Date(),
        sessionID: sessionID,
        measurements: {
          head: measurements.head,
          neck: measurements.neck,
          waist: measurements.waist,
          waist_female: measurements.waist_female || 0,
          buttocks: measurements.buttocks,
          chest: measurements.chest,
          leg_inner: measurements.leg_inner,
          leg_circ: measurements.leg_circ || 0,
          arm_outer: measurements.arm_outer,
          arm_circ: measurements.arm_circ || 0,
          body_fat: measurements.body_fat || 0,
          height: measurements.height,
        },
      };
      const saved = await saveTask(profileID, sessionID, measureData, auth);
      setMeasurementID((saved as { measurementID?: string })?.measurementID ?? sessionID);
      setIsSaved(true);
    } catch (error) {
      console.error("Failed to save measurement", error);
      // Saving starts on its own now, so a failure has to be said where the
      // result is — or the scan looks done and is not.
      saveStartedRef.current = false;
      setSaveError(
        `This scan was not saved: ${error instanceof Error ? error.message : "the request failed"}.`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const discard = async () => {
    setDiscarding(true);
    setDiscardError(null);
    try {
      await deleteMeasurement(profileID, measurementID ?? sessionID, auth);
      setDiscarded(true);
      setIsSaved(false);
    } catch (e) {
      setDiscardError(
        `The scan was not discarded: ${e instanceof Error ? e.message : "the request failed"}.`
      );
    } finally {
      setDiscarding(false);
    }
  };

  // Back to the scan page on the same session, signing in as the first scan
  // did. The API cleared the session's result when the scan was discarded, so
  // this cannot find and re-save the old one.
  const scanAgainHref = `/scan/${profileID}/${sessionID}${code ? `?code=${encodeURIComponent(code)}` : ""}`;

  // A result on screen and not stored is a scan about to be lost: ask before
  // the tab closes or reloads — while it is still saving, or if saving failed.
  useEffect(() => {
    if (!isFetched || isSaved || discarded) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isFetched, isSaved, discarded]);

  useEffect(() => {
    // if new taskId, setIsfetched to false
    setIsFetched(false);
  }, [sessionID]);

  useEffect(() => {
    let fetching = false;
    if (!isFetched) {
      const interval = setInterval(async () => {
        if (!fetching) {
          fetching = true;
          try {
            const model = await fetchModel();
            const measure = await fetchMeasurements();
            clearInterval(interval);

            const res = await getMeasurementFromModel({
              obj: String(model.obj),
              sessionID: measure.sessionID || "",
            });
            const scale = measure.scale || 1;
            console.log("Scale:", scale);
            const waist_female = res.best.slice.hullLength * scale;
            measure.waist_female = waist_female;
            const body_fat = calculateBodyFat(
              { ...measure, waist_female: waist_female },
              gender === "female" ? "female" : "male",
              "cm"
            );
            measure.body_fat = Math.floor(body_fat);

            // Weight is a per-session resource and is only posted when the user
            // entered one; age lives on the profile. Neither is on the
            // measurement, and a missing either way vetoes the estimate.
            if (showMuscleMassRef.current) {
              const [profile, weightKg] = await Promise.all([
                client.loadClientData().catch(() => undefined),
                getResource(sessionID, "weight")
                  .then((res: { value?: number }) => Number(res?.value))
                  .catch(() => NaN),
              ]);
              const muscle = calculateMuscleMass({
                weightKg,
                bodyFatPercent: measure.body_fat,
                gender: gender === "female" ? "female" : "male",
                ageYears: resolveAgeYears(profile?.age, profile?.birthDate) ?? 0,
                heightCm: measure.height,
              });
              if (muscle) {
                measure.muscle_mass = muscle.muscleMassKg;
                measure.muscle_percent = muscle.musclePercent;
              }
            }

            const finalModel = {
              ...model,
              // The model resource carries no date, and the avatar section
              // labels it with one: it read "Invalid date". This is the scan
              // that was just taken.
              createdAt: model?.createdAt || new Date().toISOString(),
              measurements: measure,
            } as ObjectModel;
            finalModel.measurements = measure;
            console.log("Final Model Measurements:", finalModel.measurements);
            setObjectModel(finalModel);
            setMeasurements(measure);
            setIsFetched(true);
            fetching = false;

            // Saved on its own, here: after every calculation above, with
            // their result, once. Not again for a scan saved before.
            if (!alreadySavedRef.current && !saveStartedRef.current) {
              saveStartedRef.current = true;
              void saveModel(measure);
            }
          } catch (error) {
            console.error("Error fetching model or measurements:", error);
          } finally {
            fetching = false;
          }
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isFetched]);

  return (
    <CaptureScreen>
      {!isFetched || !objectModel ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <Spinner size={44} />
          <p className="text-sm text-muted-foreground">Please wait…</p>
        </div>
      ) : (
        objectModel.measurements && (
          // pb-32 on a phone: room under the readings for the fixed save bar.
          <div className="mx-auto flex w-full max-w-[1296px] flex-col gap-3.5 p-4 pb-32 lg:p-7">
            {/* One scan, no target: the scrubber and the projected column
                both drop out on their own, so this is the same component
                the Avatar section uses rather than a second copy of it. */}
            <AvatarSection
              models={[objectModel]}
              goalModel={null}
              visibleMeasurements={displayedMeasurements}
            />

            {/* Fixed to the bottom on a phone, where it is always in view; in
                the flow on desktop. The scan saves on its own once the figures
                above are computed, so this says where it stands and offers the
                one thing left to decide: keep it, or discard it. */}
            <div
              data-testid="save-bar"
              className="fixed inset-x-0 bottom-0 z-40 flex flex-wrap items-center gap-2 border-t border-border bg-card px-4 pt-3 lg:static lg:rounded-lg lg:border lg:p-5"
              style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
            >
              <p
                data-testid="save-status"
                className={`w-full text-[12px] lg:w-auto lg:flex-1 ${
                  saveError || discardError ? "text-destructive" : isSaved ? "text-primary" : "text-foreground-soft"
                }`}
              >
                {discarded
                  ? "Scan discarded. It is not on the dashboard."
                  : saveError
                    ? saveError
                    : isSaved
                      ? "Saved. This scan is on the dashboard."
                      : "Saving to the dashboard…"}
                {discardError && ` ${discardError}`}
              </p>

              {saveError && !discarded && (
                <ActionButton
                  variant="primary"
                  className="px-5 py-3 text-sm"
                  disabled={isSaving}
                  onClick={() => {
                    saveStartedRef.current = true;
                    void saveModel(measurements);
                  }}
                  testId="save-retry"
                >
                  {isSaving ? "Saving…" : "Try again"}
                </ActionButton>
              )}

              {isSaved && !discarded && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      data-testid="discard-scan"
                      disabled={discarding}
                      className="rounded-lg border border-border bg-card px-5 py-3 text-sm font-medium text-destructive transition-colors hover:bg-shell disabled:opacity-50"
                    >
                      {discarding ? "Discarding…" : "Discard scan"}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Discard this scan?</AlertDialogTitle>
                      <AlertDialogDescription>
                        It will be removed from the dashboard. This can't be undone. You can
                        scan again straight after.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="discard-cancel">Keep scan</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="discard-confirm"
                        onClick={() => void discard()}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Discard scan
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {discarded && (
                <a
                  href={scanAgainHref}
                  data-testid="scan-again"
                  className="flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"
                >
                  Scan again
                  <ArrowForwardIcon size={18} />
                </a>
              )}

              {isSaved && !discarded && session?.redirect && (
                <a
                  href={session.redirect}
                  className="flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-medium transition-colors hover:bg-shell"
                >
                  {session.redirectLabel || "Continue"}
                  <ArrowForwardIcon size={18} />
                </a>
              )}

              {isSaved && !discarded && (
                <a
                  href={dashboardHrefAfterScan(profileID, isAuthenticated)}
                  data-testid="view-dashboard"
                  className="flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-medium transition-colors hover:bg-shell"
                >
                  View dashboard
                  <ArrowForwardIcon size={18} />
                </a>
              )}
            </div>
          </div>
        )
      )}
    </CaptureScreen>
  );
};

export default ResultPage;
